import BigNumber from 'bignumber.js'
import { BLOCKS_PER_YEAR, SMOY_PER_YEAR } from 'config'

const MAX_APR = 1000000

/**
 * Get the APR value in %
 * @param stakingTokenPrice Token price in the same quote currency
 * @param rewardTokenPrice Token price in the same quote currency
 * @param totalStaked Total amount of stakingToken in the pool
 * @param tokenPerBlock Amount of new smoy allocated to the pool for each new block
 * @returns Null if the APR is NaN or infinite.
 */
export const getPoolApr = (
  stakingTokenPrice: number,
  rewardTokenPrice: number,
  totalStaked: number,
  tokenPerBlock: number
): number => {
  const totalRewardPricePerYear = new BigNumber(rewardTokenPrice).times(tokenPerBlock).times(BLOCKS_PER_YEAR)
  const totalStakingTokenInPool = new BigNumber(stakingTokenPrice).times(totalStaked)
  const apr = totalRewardPricePerYear.div(totalStakingTokenInPool).times(100)
  return apr.isNaN() || !apr.isFinite() ? null : apr.toNumber()
}

/**
 * Get farm APR value in %
 * @param poolWeight allocationPoint / totalAllocationPoint
 * @param smoyPriceUsd Smoy price in USD
 * @param poolLiquidityUsd Total pool liquidity in USD
 * @returns
 */
export const getFarmApr = (poolWeight: BigNumber, smoyPriceUsd: BigNumber, poolLiquidityUsd: BigNumber): number => {
  const yearlyRewardAllocation = SMOY_PER_YEAR.times(poolWeight)
  const apr = yearlyRewardAllocation.times(smoyPriceUsd).div(poolLiquidityUsd).times(100)
  const maxAPR = 1000000000
  if (apr.isNaN() || !apr.isFinite()) return null
  if (apr.toNumber() > maxAPR) return maxAPR
  return apr.toNumber()
}

/**
 * Get farm APR value in %
 * @param poolWeight allocationPoint / totalAllocationPoint
 * @param rewardPriceUsd Vault's reward price in USD
 * @param poolLiquidityUsd Total pool liquidity in USD
 * @returns
 */
export const getVaultApr = (
  rewardPerBlock: BigNumber,
  poolWeight: BigNumber,
  rewardPriceUsd: BigNumber,
  poolLiquidityUsd: BigNumber
): number => {
  const rewardPerYear = BLOCKS_PER_YEAR.times(rewardPerBlock)
  const yearlyRewardAllocation = rewardPerYear.times(poolWeight)
  const apr = yearlyRewardAllocation.times(rewardPriceUsd).div(poolLiquidityUsd).times(100)

  if (apr.isNaN() || !apr.isFinite()) return null
  if (apr.toNumber() > MAX_APR) return MAX_APR
  return apr.toNumber()
}

export const getWinnerVaultApr = (
  rewardPerBlock: BigNumber,
  poolWeight: BigNumber,
  rewardPriceUsd: BigNumber,
  poolLiquidityUsd: BigNumber,
  estimatedPrizeUsd: BigNumber
): number => {
  const rewardPerYear = BLOCKS_PER_YEAR.times(rewardPerBlock)
  const yearlyRewardAllocation = rewardPerYear.times(poolWeight)
  const estimatedReward = yearlyRewardAllocation.times(rewardPriceUsd).plus(estimatedPrizeUsd)
  const apr = estimatedReward.div(poolLiquidityUsd).times(100)

  if (apr.isNaN() || !apr.isFinite()) return null
  if (apr.toNumber() > MAX_APR) return MAX_APR
  return apr.toNumber()
}

export const calcAPY = (apr: number, numberOfcompounding: number) => {
  if (numberOfcompounding === 0) return 0
  const apy = ((1 + (apr * 0.01) / numberOfcompounding) ** numberOfcompounding - 1) * 100.0
  if (apy > MAX_APR) return MAX_APR
  return apy
}

export default null
